import { print } from 'graphql';

import {
  DASH_META_ROOT,
  getAuthDashMetaCsrfToken,
  getLoggedInUserSnapshot,
  MetadataKey,
} from '@revelio/auth';
import { UpdateUserDocument } from '@revelio/data-access';

import { environment } from '../../../environments/environment';
import {
  PreferenceScope,
  UserPreferenceKey,
  type UserPreferences,
} from '../types';
import { filterPreferencesForScope } from '../utils/preference-helpers';

export enum EntityType {
  CLIENT = 'client',
  USER = 'user',
}

// Types matching the dash-meta API
export interface UserPreference {
  id?: string;
  entityId: string;
  entityType: EntityType;
  scope: PreferenceScope;
  preferences: Record<string, string>;
  createdAt?: string;
  updatedAt?: string;
}

export interface CreateUserPreferenceDto {
  entityType: EntityType;
  scope: PreferenceScope;
  preferences: Record<string, string>;
}

export interface UpdateUserPreferenceDto {
  entityType?: EntityType;
  scope?: PreferenceScope;
  preferences?: Record<string, string>;
}

interface KeyValue {
  key: string;
  value: string;
}

// Replicate currency preference to user metadata (delete after full transition to user preferences)
async function syncCurrencyToUserDetails(code: string): Promise<void> {
  try {
    const { id: userId } = getLoggedInUserSnapshot() ?? {};
    if (!userId) return;

    const mutation = print(UpdateUserDocument);
    const variables = {
      id: userId,
      metadata: [{ key: MetadataKey.CurrencyCode, value: code }] as KeyValue[],
    };

    await fetchJson<{ data?: unknown }>(`${environment.GO_API_ROOT}/query`, {
      method: 'POST',
      body: JSON.stringify({
        operationName: 'UpdateUser',
        query: mutation,
        variables,
      }),
    });
  } catch (err) {
    // Non-critical – log and continue without blocking preference save
    console.warn('Failed to sync currency preference to user details', err);
  }
}

async function fetchJson<T>(
  input: RequestInfo | URL,
  init?: RequestInit
): Promise<T> {
  const csrfToken = getAuthDashMetaCsrfToken();

  const response = await fetch(input, {
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'x-surf-token-dash-meta': csrfToken ?? '',
      ...(init?.headers || {}),
    },
    ...init,
  });

  if (!response.ok) {
    const message = await response.text();
    const error: Error & { status?: number } = new Error(
      message || 'Unknown error'
    );
    error.status = response.status;
    throw error;
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return await response.json();
}

// Convert UserPreferences to string-based preferences for API
function serializePreferences(
  preferences: Partial<UserPreferences>
): Record<string, string> {
  const result: Record<string, string> = {};

  Object.entries(preferences).forEach(([key, value]) => {
    if (value !== undefined) {
      result[key] = typeof value === 'string' ? value : JSON.stringify(value);
    }
  });

  return result;
}

// Convert string-based preferences from API to UserPreferences
function deserializePreferences(
  preferences: Record<string, string>
): Partial<UserPreferences> {
  const result: Partial<UserPreferences> = {};

  Object.entries(preferences).forEach(([key, value]) => {
    try {
      // Try to parse as JSON first
      result[key as keyof UserPreferences] = JSON.parse(value);
    } catch {
      // If parsing fails, skip this preference - let defaults handle it
      console.warn(`Failed to parse preference value for key "${key}":`, value);
    }
  });

  return result;
}

// Get all preferences for the current user
export const fetchPreferences = (): Promise<UserPreference[]> =>
  fetchJson<{ success: boolean; data: UserPreference[] }>(
    `${DASH_META_ROOT}/api/user-preferences`
  ).then((response) => response.data);

// Get preferences by scope
export const getPreferencesByScope = async (
  scope: PreferenceScope
): Promise<UserPreference | null> => {
  const preferences = await fetchPreferences();
  return preferences.find((p) => p.scope === scope) || null;
};

// Create new preferences
export const createPreferences = async (
  scope: PreferenceScope,
  preferences: Partial<UserPreferences>
): Promise<UserPreference> => {
  const dto: CreateUserPreferenceDto = {
    entityType: EntityType.USER,
    scope,
    preferences: serializePreferences(
      filterPreferencesForScope(scope, preferences)
    ),
  };

  return fetchJson<{ success: boolean; data: UserPreference }>(
    `${DASH_META_ROOT}/api/user-preferences`,
    {
      method: 'POST',
      body: JSON.stringify(dto),
    }
  ).then((response) => response.data);
};

// Update preferences by ID
export const updatePreferences = async (
  id: string,
  preferences: Partial<UserPreferences>
): Promise<UserPreference> => {
  const dto: UpdateUserPreferenceDto = {
    preferences: serializePreferences(preferences),
  };

  return fetchJson<{ success: boolean; data: UserPreference }>(
    `${DASH_META_ROOT}/api/user-preferences/${id}`,
    {
      method: 'POST',
      body: JSON.stringify(dto),
    }
  ).then((response) => response.data);
};

// Delete preferences by ID
export const deletePreferences = (id: string): Promise<void> =>
  fetchJson<void>(`${DASH_META_ROOT}/api/user-preferences/${id}`, {
    method: 'DELETE',
  });

// Save preferences for a scope (create or update)
export const savePreferencesForScope = async (
  scope: PreferenceScope,
  preferences: Partial<UserPreferences>
): Promise<UserPreference> => {
  const existing = await getPreferencesByScope(scope);

  // Replicate currency preference to user metadata (delete after full transition to user preferences)
  if (scope === PreferenceScope.GLOBAL) {
    const currencyCode = preferences[UserPreferenceKey.Currency]?.code;
    const existingPreferences = deserializePreferences(
      existing?.preferences || {}
    );
    const existingCurrencyCode =
      existingPreferences?.[UserPreferenceKey.Currency]?.code;
    if (currencyCode && currencyCode !== existingCurrencyCode) {
      syncCurrencyToUserDetails(currencyCode);
    }
  }

  if (existing?.id) {
    return updatePreferences(
      existing.id,
      filterPreferencesForScope(scope, preferences)
    );
  } else {
    return createPreferences(scope, preferences);
  }
};

/**
 * Fetch all preferences once and return them grouped by scope (deserialized).
 * This is used by UI code that needs to switch scopes without refetching.
 */
export const getAllPreferences = async (): Promise<
  Record<PreferenceScope, Partial<UserPreferences>>
> => {
  const all = await fetchPreferences();

  const byScope: Record<PreferenceScope, Partial<UserPreferences>> = {
    [PreferenceScope.GLOBAL]: {},
    [PreferenceScope.INDIVIDUAL]: {},
    [PreferenceScope.COMPANY]: {},
    [PreferenceScope.GEOGRAPHY]: {},
    [PreferenceScope.ROLE]: {},
  };

  all.forEach((pref) => {
    byScope[pref.scope] = filterPreferencesForScope(
      pref.scope,
      deserializePreferences(pref.preferences)
    );
  });

  return byScope;
};
